import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AchievementNotifications = ({ achievements, onShare }) => {
  const [visibleAchievements, setVisibleAchievements] = useState([]);
  const [animatingIndex, setAnimatingIndex] = useState(-1);

  useEffect(() => {
    // Animate achievements one by one
    achievements?.forEach((achievement, index) => {
      setTimeout(() => {
        setVisibleAchievements(prev => [...prev, achievement]);
        setAnimatingIndex(index);
        setTimeout(() => setAnimatingIndex(-1), 600);
      }, index * 500);
    });
  }, [achievements]);

  const getBadgeColor = (type) => {
    switch (type) {
      case 'gold':
        return 'bg-gradient-to-br from-yellow-400 to-yellow-600';
      case 'silver':
        return 'bg-gradient-to-br from-gray-300 to-gray-500';
      case 'bronze':
        return 'bg-gradient-to-br from-orange-400 to-orange-600';
      case 'milestone':
        return 'bg-gradient-to-br from-purple-400 to-purple-600';
      case 'streak':
        return 'bg-gradient-to-br from-green-400 to-green-600';
      default:
        return 'bg-gradient-to-br from-blue-400 to-blue-600';
    }
  };

  const getAchievementIcon = (category) => {
    switch (category) {
      case 'completion':
        return 'Trophy';
      case 'speed':
        return 'Zap';
      case 'quality':
        return 'Award';
      case 'streak':
        return 'Flame';
      case 'milestone':
        return 'Star';
      case 'mastery':
        return 'Crown';
      default:
        return 'Medal';
    }
  };

  if (achievements?.length === 0) {
    return null;
  }

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="Trophy" size={24} color="var(--color-warning)" />
        <h2 className="text-xl font-semibold text-foreground">Achievements Unlocked!</h2>
      </div>
      <div className="space-y-4">
        {visibleAchievements?.map((achievement, index) => (
          <div
            key={achievement?.id}
            className={`border border-subtle rounded-lg p-4 transition-all duration-600 ${
              animatingIndex === index 
                ? 'scale-105 shadow-elevation-2 border-warning/50' 
                : 'scale-100'
            }`}
          >
            <div className="flex items-start gap-4">
              {/* Badge */}
              <div className="flex-shrink-0">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getBadgeColor(achievement?.type)} shadow-elevation-1`}>
                  <Icon 
                    name={getAchievementIcon(achievement?.category)} 
                    size={24} 
                    color="white" 
                  />
                </div>
                {achievement?.isNew && (
                  <div className="mt-2 text-center">
                    <span className="px-2 py-1 bg-warning/20 text-warning text-xs font-medium rounded-full">
                      NEW!
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">
                      {achievement?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {achievement?.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-warning">
                      +{achievement?.points}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      XP Points
                    </div>
                  </div>
                </div>

                {/* Progress Bar (for progressive achievements) */}
                {achievement?.progress && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="text-foreground font-medium">
                        {achievement?.progress?.current}/{achievement?.progress?.total}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-warning transition-all duration-700"
                        style={{ 
                          width: `${(achievement?.progress?.current / achievement?.progress?.total) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Achievement Details */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    <span>Earned: {achievement?.earnedAt}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Users" size={12} />
                    <span>{achievement?.rarity}% of users have this</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Share"
                    iconPosition="left"
                    onClick={() => onShare(achievement)}
                  >
                    Share Achievement
                  </Button>
                  
                  {achievement?.nextLevel && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="ArrowRight" size={14} />
                      <span>Next: {achievement?.nextLevel?.title}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary Stats */}
      <div className="mt-6 pt-6 border-t border-subtle">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">
              {achievements?.reduce((sum, a) => sum + a?.points, 0)}
            </div>
            <div className="text-sm text-muted-foreground">Total XP Earned</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              {achievements?.filter(a => a?.type === 'gold')?.length}
            </div>
            <div className="text-sm text-muted-foreground">Gold Badges</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">
              {achievements?.filter(a => a?.category === 'streak')?.length}
            </div>
            <div className="text-sm text-muted-foreground">Streak Achievements</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-secondary">
              {achievements?.filter(a => a?.category === 'milestone')?.length}
            </div>
            <div className="text-sm text-muted-foreground">Milestones</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AchievementNotifications;